import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getStartrails, preloadStartrails } from '../startrails-assets.js';

// Contrat du rig partagé : interdit. La lumière est celle des étoiles.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'startrails',
  family: 'photographic',
  danceability: 0.20,
  requiresTier: 0,
  energyLevel: 0.12,
  bpmMin: 40,
  bpmMax: 110,
  movement: 0.10,
  tonality: 0.90,
  enabled: true
};

// STARTRAILS : une arche de roche dans le désert, la nuit, et le filé
// d'étoiles autour du pôle céleste. Ambient, post-rock, néo-classique,
// musique de film. Règles de kiosk-app/CLAUDE.md (R1 à R17).
//
// LA PHOTO et son masque de ciel viennent de tools/build-startrails.mjs (seuil
// sur le BLEU plus un plancher de luminance, remplissage depuis le haut,
// graine dans l'arche, bord de 1,5 px). Le ciel de la photo est assombri de
// 30 % (multiplication, dans le masque) pour que les arcs ressortent.
//
// UN ARC DE FILÉ N'EST PAS UN SEGMENT QUI SE DÉPLACE. La version précédente
// traçait [tête - longueur, tête] avec une longueur fixe : les arcs tournaient
// en bloc, une image qui pivote. Ici, comme sur une vraie série de photos :
//  - le ciel tourne (rotation R, vitesse constante pendant une pose) ;
//  - une POSE commence à la rotation S : chaque étoile y a un DÉPART fixe,
//    sa TÊTE avance de R - S ; l'arc va du départ à la tête et grandit
//    depuis un point. Le départ est l'angle de l'étoile dans le catalogue
//    MOINS la moitié de la longueur finale de la pose : la pose est centrée
//    sur les positions du catalogue. Partir de la rotation cumulée faisait
//    tourner le champ d'étoiles avec les poses, et le catalogue ne couvre
//    que le ciel de la photo : après deux poses, un secteur entier du ciel
//    (en bas à droite) n'avait plus une étoile ;
//  - la luminosité le long de l'arc suit l'ÂGE du point (le temps écoulé
//    depuis le passage de la tête, rapporté à la durée de la pose) : pleine
//    sur les 70 % les plus récents, fondu vers la queue ;
//  - une pose dure 16 mesures (45 s sans BPM) ; sa longueur finale suit
//    l'énergie lissée (20° calme, 45° intense), d'où la vitesse du ciel
//    pendant cette pose ;
//  - à la fin d'une pose, son filé se fige et s'efface en 8 s pendant que la
//    suivante grandit : deux poses au plus à l'écran, deux draw calls (même
//    géométrie, deux jeux d'uniforms : départ, fin, opacité, numéro) ;
//  - chaque pose montre 60 % des étoiles, tirées par hachage (graine de
//    l'étoile, numéro de pose) : le ciel change d'une pose à l'autre.
//
// LE PÔLE n'est plus une constante du catalogue : il est posé à l'écran, à
// 25 % de la largeur et 18 % de la hauteur depuis le coin haut gauche (hors
// des zones de R6), converti en pixels de l'image par le recadrage de l'écran
// courant. Le catalogue garde les étoiles en polaire autour de l'ancien pôle
// du build ((1010, 70)) : le vertex shader les ramène en pixels de l'image
// puis en polaire autour du nouveau pôle - aucun rebuild des textures.
//
// MAGNITUDES (rang de brillance du catalogue) : 5 % brillantes (2 px,
// luminance 0,9), 25 % moyennes (1,5 px, 0,5), 70 % faibles (1 px, 0,2).
// COULEURS : blanc-bleuté pour la plupart, 15 % ambrées ou orangées, 3 %
// rouges ; les brillantes varient légèrement de teinte le long de l'arc
// (scintillement atmosphérique).
//
// LE RENDU : un RUBAN annulaire par étoile, instancié ; le fragment calcule
// la distance exacte à l'arc en pixels d'écran (radiale dans l'arc, à
// l'extrémité au-delà) et l'angle le long de l'arc vient d'un varying
// interpolé (pas d'atan par pixel). Un arc dont le départ est fixe ne bouge
// pas : seul son bout avance.
//
// LA MUSIQUE : aucune impulsion (danceability 0,20, R9). L'énergie lissée sur
// 8 mesures règle la longueur de la pose suivante, rien d'autre. Une étoile
// filante traverse le ciel sur chaque drop, ou toutes les 8 phrases ; elle ne
// laisse rien dans les poses.

const TAU = Math.PI * 2;
const OLD_POLE = [1010, 70];        // le pôle du catalogue (tools/build-startrails.mjs, POLE)
const POLE_SCREEN = [0.25, 0.18];   // le pôle, en fractions de l'écran depuis le coin haut gauche
const POSE_BARS = 16;
const POSE_NO_BPM_S = 45;
const POSE_FADE_S = 8;
const POSE_SHARE = 0.60;            // part des étoiles visibles dans une pose
const SWEEP_CALM_DEG = 20, SWEEP_INTENSE_DEG = 45;
const RECENT = 0.70;                // la part la plus récente de l'arc, à pleine intensité
const SKY_DARKEN = 0.30;
// le ruban déborde de l'arc de la demi-largeur max (1) + bord (0,6) + 0,2 : à 2,6 px le
// Pi traçait 5 millions de fragments par frame au tier 1 (56 ms)
const MARGIN_SCREEN_PX = 1.8;
const SEGMENTS = 16;                // 45° en 16 : la corde extérieure s'écarte de 0,4 px au plus, sous la marge
const ENERGY_BARS = 8;
const METEOR_S = 0.9;
const METEOR_PHRASES = 8;
const NO_BPM = 70;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SAFE_FLOOR = 0.6;             // le chunk safeZone, sur les étoiles seulement
const FOCUS = [0.72, 0.62];         // le recadrage garde l'arche (fractions de l'image)
// les magnitudes : part, largeur (px), luminance
const MAGS = [[0.05, 2.0, 0.9], [0.25, 1.5, 0.5], [0.70, 1.0, 0.2]];

// The star counts are set by R10, not by cost: the photo's sky alone, darkened
// by 30 %, averages 0.17, and the sky must stay under 0.25. A 45-degree pose
// (centred on the catalogue) adds 0.143 for 900 stars at the brief's
// magnitudes, measured in the harness - linear in the count: 500 stars add
// 0.079 (sky 0.25). (The v2 counts, 1 500 / 2 200 / 3 000, covered the sky
// in a white sheet: 0.44 to 0.57.) ?startrailsn=<n> overrides, for tests.
const TIER = { 0: { stars: 400 }, 1: { stars: 450 }, 2: { stars: 500 } };
const STARS_OVERRIDE = (typeof location !== 'undefined') ? parseInt(new URLSearchParams(location.search).get('startrailsn'), 10) : NaN;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hash1(n) { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); }

// The part of the image on screen ("cover", centred on FOCUS): x0, y0,
// width, height in image px.
export function cropFor(imgW, imgH, aspect) {
  const ia = imgW / imgH;
  if (aspect >= ia) {
    const h = imgW / aspect;
    const y0 = Math.max(0, Math.min(imgH - h, FOCUS[1] * imgH - h / 2));
    return [0, y0, imgW, h];
  }
  const w = imgH * aspect;
  const x0 = Math.max(0, Math.min(imgW - w, FOCUS[0] * imgW - w / 2));
  return [x0, 0, w, imgH];
}
// the pole for a crop, in image px
export function poleFor(crop) {
  return [crop[0] + POLE_SCREEN[0] * crop[2], crop[1] + POLE_SCREEN[1] * crop[3]];
}
// the visibility of star `seed` in pose `pose` - the same hash as the vertex shader
export function inPose(seed, pose) {
  const x = Math.sin(seed * 91.17 + pose * 7.31) * 43758.5453;
  return x - Math.floor(x) < POSE_SHARE;
}

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const photoFragmentShader = `
  precision highp float;
  uniform sampler2D uPhoto;
  uniform sampler2D uSky;
  uniform vec4 uCrop;        // x0, y0, w, h in image px
  uniform vec2 uImg;
  uniform vec2 uPole;
  uniform float uShowMask;
  uniform float uShowPole;
  varying vec2 vUv;
  void main() {
    vec2 img = vec2(uCrop.x + vUv.x * uCrop.z, uCrop.y + (1.0 - vUv.y) * uCrop.w);
    vec2 uv = vec2(img.x / uImg.x, 1.0 - img.y / uImg.y);
    vec3 c = texture2D(uPhoto, uv).rgb;
    float sky = texture2D(uSky, uv).r;
    c *= 1.0 - ${SKY_DARKEN.toFixed(2)} * sky;                     // le ciel assombri de 30 %
    if (uShowMask > 0.5) c = mix(c * 0.35, vec3(0.9, 0.15, 0.1), sky * 0.8);
    if (uShowPole > 0.5) {
      // le pôle (jaune) et les zones d'exclusion de R6 (bleu)
      float zone = 0.0;
      if (vUv.x > 0.78 && vUv.y > 0.88) zone = 1.0;               // haut droite 22 % x 12 %
      if (vUv.x < 0.08 && vUv.y > 0.88) zone = 1.0;               // haut gauche 8 % x 12 %
      if (vUv.x > 0.78 && vUv.y < 0.10) zone = 1.0;               // bas droite 22 % x 10 %
      c = mix(c, vec3(0.1, 0.35, 1.0), zone * 0.45);
      float dp = length(img - uPole) / uCrop.w * 1600.0;
      c = mix(c, vec3(1.0, 0.9, 0.1), 1.0 - smoothstep(5.0, 7.0, dp));
    }
    gl_FragColor = vec4(c, 1.0);
  }
`;

// One ribbon per star: an annular band from the pose's DEPARTURE to the
// star's current head, widened by the margin on every side (radially, and in
// angle beyond both ends). A star outside the pose's 60 % collapses out of
// the clip volume.
const trailVertexShader = `
  precision highp float;
  uniform vec2 uPole;        // image px
  uniform vec2 uOldPole;
  uniform vec4 uCrop;
  uniform float uScale;      // screen px per image px
  uniform float uStart;      // the sky's rotation at the pose's start, radians
  uniform float uSweep;      // rotation since then (the arcs' length), radians
  uniform float uPose;       // the pose's number (the subset)
  attribute float aT;        // 0 (departure) .. 1 (head)
  attribute float aSide;     // -1 inner edge, +1 outer edge
  attribute vec4 iStar;      // catalogue radius and angle (old pole), luminance, width px
  attribute vec4 iCol;       // rgb, seed
  varying vec2 vImg;
  varying vec4 vStar;        // radius (new pole), luminance, width, seed
  varying vec3 vCol;
  varying float vDth;        // angle from the departure, interpolated (no atan per pixel)
  void main() {
    float pick = fract(sin(iCol.w * 91.17 + uPose * 7.31) * 43758.5453);
    if (pick >= ${POSE_SHARE.toFixed(2)}) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    // the catalogue position, then polar around the current pole
    vec2 p = uOldPole + iStar.x * vec2(cos(iStar.y), -sin(iStar.y));
    vec2 d = vec2(p.x - uPole.x, uPole.y - p.y);
    float r = max(length(d), 1.0);
    float a0 = atan(d.y, d.x) + uStart;          // the departure
    float m = ${MARGIN_SCREEN_PX.toFixed(2)} / uScale;
    float ext = min(m / r, 3.0);
    float a = a0 - ext + aT * (uSweep + 2.0 * ext);
    float rr = max(0.0, r + aSide * m);
    vec2 img = vec2(uPole.x + rr * cos(a), uPole.y - rr * sin(a));
    vImg = img; vStar = vec4(r, iStar.z, iStar.w, iCol.w); vCol = iCol.rgb;
    vDth = a - a0;
    vec2 uv = vec2((img.x - uCrop.x) / uCrop.z, 1.0 - (img.y - uCrop.y) / uCrop.w);
    gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0);
  }
`;

// The exact distance to the arc, in screen px: radial inside the arc, to the
// nearest end beyond. highp: a pixel's angle around a pole 1000 image px away
// must be good to a few hundredths of a pixel.
const trailFragmentShader = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif
  uniform sampler2D uSky;
  uniform vec4 uCrop;
  uniform vec2 uImg;
  uniform vec2 uPole;
  uniform float uScale;
  uniform float uSweep;
  uniform float uOpacity;
  uniform float uLive;       // 1: the pose being exposed (the star is at the head)
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vImg;
  varying vec4 vStar;
  varying vec3 vCol;
  varying float vDth;

  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    float sky = texture2D(uSky, vec2(vImg.x / uImg.x, 1.0 - vImg.y / uImg.y)).r;
    if (sky < 0.004) discard;
    vec2 d = vec2(vImg.x - uPole.x, uPole.y - vImg.y);
    float r = length(d);                                     // exact: the radial profile never moves
    float dth = vDth;
    float dr = abs(r - vStar.x);
    float beyond = (dth < 0.0 ? -dth : max(0.0, dth - uSweep)) * vStar.x;
    float dist = sqrt(dr * dr + beyond * beyond) * uScale;   // screen px
    float hw = 0.5 * vStar.z;                                // half of 1, 1.5 or 2 px
    float line = 1.0 - smoothstep(hw - 0.6, hw + 0.6, max(dist, 0.0));
    // THE AGE: 0 at the departure, 1 at the head; full over the 70 % most
    // recent, a fade toward the tail. An arc still shorter than a few px is
    // one point, at full light.
    float u = clamp(dth / max(uSweep, 1e-5), 0.0, 1.0);
    float sweepPx = uSweep * vStar.x * uScale;
    float age = mix(1.0, smoothstep(0.0, ${(1 - RECENT).toFixed(2)}, u), clamp((sweepPx - 2.0) / 6.0, 0.0, 1.0));
    // the head of the pose being exposed: the star itself, a touch brighter
    float toHead = (dth <= uSweep ? uSweep - dth : 0.0) * vStar.x * uScale;
    float head = uLive * exp(-toHead / 2.5);
    float gq = max(0.0, 1.0 - dist / 1.6);
    // the brightest core (0,9 x 1,05) stays at the bloom threshold: no haze blobs
    float k = vStar.y * max(line * age * (1.0 + 0.05 * head), 0.30 * gq * gq * head);
    if (k <= 0.0) discard;
    // the bright stars change hue slightly along the arc (atmospheric twinkle)
    vec3 col = vCol;
    if (vStar.y > 0.8) {
      float w = 0.5 + 0.5 * sin(dth * vStar.x * 0.11 + vStar.w * 40.0);
      col = mix(col, col * vec3(1.10, 0.94, 0.82), w * 0.7);
    }
    vec2 uv = vec2((vImg.x - uCrop.x) / uCrop.z, 1.0 - (vImg.y - uCrop.y) / uCrop.w);
    gl_FragColor = vec4(col * k * uOpacity * sky * safeAtt(uv), 1.0);
  }
`;

// The shooting star: a full-screen quad, visible only for its 0.9 s.
const meteorFragmentShader = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif
  uniform sampler2D uSky;
  uniform vec4 uCrop;
  uniform vec2 uImg;
  uniform float uScale;
  uniform vec4 uMeteorA;     // from (x, y), to (x, y), image px
  uniform float uMeteorT;    // 0..1
  varying vec2 vUv;
  void main() {
    vec2 img = vec2(uCrop.x + vUv.x * uCrop.z, uCrop.y + (1.0 - vUv.y) * uCrop.w);
    vec2 a = uMeteorA.xy, b = uMeteorA.zw;
    vec2 hd = mix(a, b, uMeteorT), tl = mix(a, b, max(0.0, uMeteorT - 0.28));
    vec2 sg = hd - tl;
    float h = clamp(dot(img - tl, sg) / max(dot(sg, sg), 1e-3), 0.0, 1.0);
    float dm = length(img - tl - sg * h) * uScale;
    float m = (1.0 - smoothstep(0.3, 1.5, dm)) * h * h * sin(3.1415927 * uMeteorT);
    if (m <= 0.0) discard;
    float sky = texture2D(uSky, vec2(img.x / uImg.x, 1.0 - img.y / uImg.y)).r;
    gl_FragColor = vec4(vec3(0.9, 0.95, 1.0) * m * 1.1 * sky, 1.0);
  }
`;

// The star classes, from the catalogue: magnitude by brightness RANK (the 5 %
// brightest are the bright ones), colour by a hash of the index.
export function starClasses(stars, n) {
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => stars[b * 5 + 2] - stars[a * 5 + 2]);
  const mag = new Int8Array(n);
  order.forEach((i, k) => { const f = k / n; mag[i] = f < MAGS[0][0] ? 0 : (f < MAGS[0][0] + MAGS[1][0] ? 1 : 2); });
  const col = [];
  for (let i = 0; i < n; i++) {
    const h = hash1(i * 3.7 + 1.3), v = hash1(i * 5.1 + 7.9);
    let c;
    if (h < 0.03) c = [1.0, 0.42, 0.30];                                   // rouges, quelques-unes
    else if (h < 0.18) c = v < 0.5 ? [1.0, 0.74, 0.42] : [1.0, 0.62, 0.30]; // ambrées, orangées (15 %)
    else c = [0.80 + 0.10 * v, 0.88 + 0.06 * v, 1.0];                     // blanc-bleuté
    col.push(c);
  }
  return { mag, col };
}

// The ribbon: SEGMENTS + 1 pairs of vertices (departure -> head, inner/outer),
// one instance per star.
export function trailGeometry(stars, n) {
  const g = new THREE.InstancedBufferGeometry();
  const aT = new Float32Array((SEGMENTS + 1) * 2), aSide = new Float32Array((SEGMENTS + 1) * 2), pos = new Float32Array((SEGMENTS + 1) * 2 * 3);
  const idx = [];
  for (let j = 0; j <= SEGMENTS; j++) {
    aT[j * 2] = aT[j * 2 + 1] = j / SEGMENTS;
    aSide[j * 2] = -1; aSide[j * 2 + 1] = 1;
    if (j < SEGMENTS) { const a = j * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  }
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aT', new THREE.BufferAttribute(aT, 1));
  g.setAttribute('aSide', new THREE.BufferAttribute(aSide, 1));
  g.setIndex(idx);
  const cls = starClasses(stars, n);
  const inst = new Float32Array(n * 4), icol = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const m = MAGS[cls.mag[i]];
    inst[i * 4] = stars[i * 5]; inst[i * 4 + 1] = stars[i * 5 + 1]; inst[i * 4 + 2] = m[2]; inst[i * 4 + 3] = m[1];
    const c = cls.col[i];
    icol[i * 4] = c[0]; icol[i * 4 + 1] = c[1]; icol[i * 4 + 2] = c[2]; icol[i * 4 + 3] = (i * 0.618034) % 1 * 100;
  }
  g.setAttribute('iStar', new THREE.InstancedBufferAttribute(inst, 4));
  g.setAttribute('iCol', new THREE.InstancedBufferAttribute(icol, 4));
  g.instanceCount = n;
  g._classes = cls;
  return g;
}

export class StartrailsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    const p = parseFloat(urlParam('startrailspose'));
    this._forcePose = Number.isFinite(p) ? clamp01(p) : null;
    this._showMask = urlParam('startrailsmask') === '1';
    this._showPole = urlParam('startrailspole') === '1';
    this._post = { bloomStrength: 0.15, bloomThreshold: 0.95, grain: 0, vignette: 0.25, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('startrailsprobe') === '1' && typeof window !== 'undefined') window.__startrails = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    this._meshes = [];
    this._assets = getStartrails();
    if (this._assets) { this._build(this._assets); return; }
    // NOT PRELOADED by this entry point (the console's player-boot did not
    // know Startrails: a black screen). Load now and build when it arrives;
    // only a real failure leaves the frame empty.
    const self = this;
    preloadStartrails().then(function (ok) {
      if (!self._scene) return;                       // disposed meanwhile
      if (ok) { self._assets = getStartrails(); self._build(self._assets); }
      // eslint-disable-next-line no-console
      else console.warn('[startrails] assets unavailable');
    });
  }

  _build(A) {
    const scene = this._scene;
    const M = A.manifest;
    this._n = Math.min(Number.isFinite(STARS_OVERRIDE) ? STARS_OVERRIDE : TIER[this._tier].stars, A.stars.length / 5);
    const img = new THREE.Vector2(M.size[0], M.size[1]);
    this._crop = new THREE.Vector4(0, 0, M.size[0], M.size[1]);
    const oldPole = M.pole || OLD_POLE;
    this._pole = new THREE.Vector2(oldPole[0], oldPole[1]);
    this._photoU = { uPhoto: { value: A.photo }, uSky: { value: A.sky }, uCrop: { value: this._crop }, uImg: { value: img }, uPole: { value: this._pole },
      uShowMask: { value: this._showMask ? 1 : 0 }, uShowPole: { value: this._showPole ? 1 : 0 } };
    const shared = {
      uSky: { value: A.sky }, uCrop: { value: this._crop }, uImg: { value: img },
      uPole: { value: this._pole }, uOldPole: { value: new THREE.Vector2(oldPole[0], oldPole[1]) }, uScale: { value: 1 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._shared = shared;
    this._meteorU = { uSky: { value: A.sky }, uCrop: { value: this._crop }, uImg: { value: img }, uScale: shared.uScale,
      uMeteorA: { value: new THREE.Vector4() }, uMeteorT: { value: 0 } };
    const photo = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._photoU, vertexShader: quadVertexShader, fragmentShader: photoFragmentShader, depthTest: false, depthWrite: false
    }));
    photo.frustumCulled = false; photo.renderOrder = -1000;
    // two poses at most: one geometry, two materials (their own departure,
    // sweep, opacity and number)
    this._trailGeo = trailGeometry(A.stars, this._n);
    this._slots = [];
    for (let k = 0; k < 2; k++) {
      const U = Object.assign({}, shared, { uStart: { value: 0 }, uSweep: { value: 0 }, uPose: { value: 0 }, uOpacity: { value: 0 }, uLive: { value: 0 } });
      const mesh = new THREE.Mesh(this._trailGeo, new THREE.ShaderMaterial({
        uniforms: U, vertexShader: trailVertexShader, fragmentShader: trailFragmentShader,
        transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
      }));
      mesh.frustumCulled = false; mesh.renderOrder = -999 + k; mesh.visible = false;
      this._slots.push({ mesh, U, pose: null });
    }
    const meteor = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._meteorU, vertexShader: quadVertexShader, fragmentShader: meteorFragmentShader,
      transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
    }));
    meteor.frustumCulled = false; meteor.renderOrder = -990; meteor.visible = false;
    this._meteor = meteor;
    const self = this;
    photo.onBeforeRender = function (renderer) { self._layout(renderer); };
    scene.add(photo); scene.add(this._slots[0].mesh); scene.add(this._slots[1].mesh); scene.add(meteor);
    this._meshes = [photo, this._slots[0].mesh, this._slots[1].mesh, meteor];

    this._time = 0;
    this._rot = 0;
    this._omega = 0;
    this._energy = null;
    this._beats = 0;
    this._poses = [];            // { n, start, end, sweepMax, born, ended }
    this._poseCount = 0;
    this._phasePhrase = null; this._phrases = 0; this._lastMeteorPhrase = 0;
    this._meteorAt = -1; this._meteorSeed = 7;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._log = [];
    this._bpm = NO_BPM; this._hasBpm = false;
    this._startPose();
    this._writeSlots();
  }

  // The crop, the pole and the px scale follow the target being rendered NOW
  // (the composer's), not the canvas.
  _layout(renderer) {
    const rt = renderer.getRenderTarget();
    const size = rt ? _size.set(rt.width, rt.height) : renderer.getDrawingBufferSize(_size);
    const M = this._assets.manifest;
    const c = cropFor(M.size[0], M.size[1], size.x / Math.max(1, size.y));
    this._crop.set(c[0], c[1], c[2], c[3]);
    const p = poleFor(c);
    this._pole.set(p[0], p[1]);
    this._shared.uScale.value = size.y / c[3];
  }

  // A new exposure: the stars start from where they are; the previous pose
  // freezes and fades.
  _startPose() {
    const e = this._energy == null ? 0.5 : this._energy;
    const sweepMax = (SWEEP_CALM_DEG + (SWEEP_INTENSE_DEG - SWEEP_CALM_DEG) * clamp01(e)) * Math.PI / 180;
    const dur = this._hasBpm ? POSE_BARS * 4 * 60 / this._bpm : POSE_NO_BPM_S;
    for (const p of this._poses) if (p.ended == null) { p.ended = this._time; p.end = this._rot; }
    this._poseCount++;
    this._poses.push({ n: this._poseCount, start: this._rot, end: this._rot, offset: -sweepMax / 2, sweepMax, dur, born: this._time, ended: null });
    while (this._poses.length > 2) this._poses.shift();
    this._omega = sweepMax / dur;
    this._log.push({ t: +this._time.toFixed(2), what: 'pose', n: this._poseCount, deg: +(sweepMax * 180 / Math.PI).toFixed(1), s: +dur.toFixed(1) });
  }

  _writeSlots() {
    // the live pose in slot 1 (drawn last), the fading one in slot 0
    const live = this._poses.find((p) => p.ended == null) || null;
    const old = this._poses.find((p) => p.ended != null) || null;
    const set = (slot, p, isLive) => {
      if (!p) { slot.mesh.visible = false; slot.pose = null; return; }
      const op = isLive ? 1 : clamp01(1 - (this._time - p.ended) / POSE_FADE_S);
      slot.mesh.visible = op > 0.001;
      slot.U.uStart.value = p.offset;
      slot.U.uSweep.value = Math.max(0, (isLive ? this._rot : p.end) - p.start);
      slot.U.uPose.value = p.n % 997;
      slot.U.uOpacity.value = op;
      slot.U.uLive.value = isLive ? 1 : 0;
      slot.pose = p;
    };
    set(this._slots[0], old, false);
    set(this._slots[1], live, true);
    this._poses = this._poses.filter((p) => p.ended == null || this._time - p.ended < POSE_FADE_S);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _rand() { this._meteorSeed = (this._meteorSeed * 1664525 + 1013904223) >>> 0; return this._meteorSeed / 4294967296; }

  // A shooting star across the upper sky, from one side to the other, slanting
  // down.
  _launchMeteor(why) {
    const M = this._assets.manifest, W = M.size[0], H = M.size[1];
    const left = this._rand() < 0.5;
    const x0 = left ? W * (0.05 + 0.25 * this._rand()) : W * (0.70 + 0.25 * this._rand());
    const y0 = H * (0.06 + 0.22 * this._rand());
    const len = W * (0.22 + 0.12 * this._rand()), ang = (0.25 + 0.25 * this._rand()) * (left ? 1 : -1);
    const dx = Math.cos(ang) * len * (left ? 1 : -1), dy = Math.abs(Math.sin(ang)) * len;
    this._meteorU.uMeteorA.value.set(x0, y0, x0 + dx, y0 + dy);
    this._meteorAt = this._time;
    this._log.push({ t: +this._time.toFixed(2), what: 'meteor', why });
  }

  update(deltaTime, profile) {
    if (!this._assets || !this._slots) return;
    if (profile.playbackState !== 'PLAYING') return;        // frozen: the exposure pauses, lit
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._time += dt;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._hasBpm = bpm > 0;
    this._bpm = bpm > 0 ? bpm : NO_BPM;
    const barS = 240 / this._bpm;
    // the energy over 8 bars: it only sets the NEXT pose's length
    const e = profile.director && Number.isFinite(profile.director.intensity) ? clamp01(profile.director.intensity) : 0.5;
    if (this._energy == null) this._energy = e;
    this._energy += (e - this._energy) * (1 - Math.exp(-dt / (ENERGY_BARS * barS)));

    // the sky turns at the live pose's speed; the pose ends at its length
    const live = this._poses.find((p) => p.ended == null);
    if (this._forcePose != null && live) {
      this._rot = live.start + this._forcePose * live.sweepMax;
    } else {
      this._rot += this._omega * dt;
      if (live && (this._rot - live.start >= live.sweepMax || this._time - live.born >= live.dur)) this._startPose();
    }
    this._writeSlots();

    // the shooting star: on a drop, or every 8 phrases
    this._beats += dt * this._bpm / 60;
    const ph = profile.phrasePhase;
    let phraseEdge = false;
    if (ph != null && profile.beatPhase != null) {
      if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phraseEdge = true;
      this._phasePhrase = ph;
    } else if (Math.floor(this._beats / 16) !== Math.floor((this._beats - dt * this._bpm / 60) / 16)) phraseEdge = true;
    if (phraseEdge) this._phrases++;
    const meteorBusy = this._meteorAt >= 0;
    if (!meteorBusy && this._tickDrops(profile)) this._launchMeteor('drop');
    else if (!meteorBusy && phraseEdge && this._phrases - this._lastMeteorPhrase >= METEOR_PHRASES) { this._lastMeteorPhrase = this._phrases; this._launchMeteor('phrases'); }
    if (this._meteorAt >= 0) {
      const t = (this._time - this._meteorAt) / METEOR_S;
      this._meteorU.uMeteorT.value = Math.min(t, 1);
      this._meteor.visible = t < 1;
      if (t >= 1) this._meteorAt = -1;
    }
  }

  getProbe() {
    return {
      time: +(this._time || 0).toFixed(2), rotDeg: +((this._rot || 0) * 180 / Math.PI).toFixed(2), omegaDegS: +((this._omega || 0) * 180 / Math.PI).toFixed(3),
      pole: this._pole ? [+this._pole.x.toFixed(1), +this._pole.y.toFixed(1)] : null, crop: this._crop ? this._crop.toArray().map((v) => +v.toFixed(1)) : null,
      poses: (this._poses || []).map((p) => ({ n: p.n, sweepDeg: +(((p.ended == null ? this._rot : p.end) - p.start) * 180 / Math.PI).toFixed(2), maxDeg: +(p.sweepMax * 180 / Math.PI).toFixed(1), ended: p.ended })),
      slots: (this._slots || []).map((s) => ({ visible: s.mesh.visible, pose: s.U.uPose.value, opacity: +s.U.uOpacity.value.toFixed(3), sweep: +s.U.uSweep.value.toFixed(4), start: +s.U.uStart.value.toFixed(4) })),
      log: (this._log || []).slice(-12)
    };
  }

  dispose() {
    for (const m of this._meshes || []) {
      m.onBeforeRender = null;
      if (this._scene) this._scene.remove(m);
      m.material.dispose();
    }
    if (this._meshes && this._meshes.length) { this._meshes[0].geometry.dispose(); this._meshes[3].geometry.dispose(); }
    if (this._trailGeo) this._trailGeo.dispose();
    if (typeof window !== 'undefined' && window.__startrails === this) window.__startrails = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
